import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  X, 
  ChevronRight, 
  Sparkles, 
  Wand2, 
  Code2, 
  MessageSquare, 
  Image,
  Trophy,
  Star,
  Zap,
  PartyPopper,
  Check,
  Shield,
  GraduationCap,
  Palette,
  Briefcase,
} from 'lucide-react';
import { usePersona, PERSONAS, PersonaType } from '@/hooks/usePersona';
import confetti from 'canvas-confetti';
import { useOnboarding } from '@/hooks/useOnboarding';
import { useAnalytics } from '@/hooks/useAnalytics';
import { cn } from '@/lib/utils';

interface ToolOption {
  id: string;
  name: string;
  description: string;
  icon: typeof Wand2;
  route: string;
  color: string;
}

const TOOL_OPTIONS: ToolOption[] = [
  {
    id: 'chat',
    name: 'AI Chat',
    description: 'Have a conversation with our AI assistant',
    icon: MessageSquare,
    route: '/chat',
    color: 'from-blue-500 to-cyan-500',
  },
  {
    id: 'particles',
    name: 'GenieBot Avatar',
    description: 'Interactive particle avatar with face tracking',
    icon: Sparkles,
    route: '/geniebot-particles',
    color: 'from-purple-500 to-pink-500',
  },
  {
    id: 'webdesign',
    name: 'WebDesign',
    description: 'Generate UI specifications with AI',
    icon: Code2,
    route: '/tools/ui-spec',
    color: 'from-green-500 to-emerald-500',
  },
  {
    id: 'image',
    name: 'Image Generator',
    description: 'Create images from text prompts',
    icon: Image,
    route: '/ai-image-generator',
    color: 'from-orange-500 to-amber-500',
  },
  {
    id: 'contract',
    name: 'Contract Analyzer',
    description: 'Spot risky clauses in an agreement',
    icon: Shield,
    route: '/tools/contract-analyzer',
    color: 'from-red-500 to-rose-500',
  },
  {
    id: 'security-checklist',
    name: 'Security Checklist',
    description: 'Harden an AI tool server step by step',
    icon: Shield,
    route: '/security-checklist',
    color: 'from-slate-500 to-zinc-600',
  },
  {
    id: 'study',
    name: 'Study Helper',
    description: 'Turn notes into flashcards and summaries',
    icon: GraduationCap,
    route: '/tools',
    color: 'from-sky-500 to-indigo-500',
  },
  {
    id: 'prompt-factory',
    name: 'Prompt Template Factory',
    description: 'Build a reusable prompt you can share',
    icon: Wand2,
    route: '/prompt-template-factory',
    color: 'from-violet-500 to-fuchsia-500',
  },
];

const byId = (id: string) => TOOL_OPTIONS.find((t) => t.id === id)!;

/** One clear "first win" per persona — the menu is the product. */
const FIRST_WIN: Record<PersonaType, { headline: string; tools: ToolOption[] }> = {
  general: {
    headline: 'Start with something quick and useful',
    tools: [byId('chat'), byId('image'), byId('prompt-factory'), byId('particles')],
  },
  security: {
    headline: 'Your first win: review something risky',
    tools: [byId('contract'), byId('security-checklist'), byId('chat'), byId('prompt-factory')],
  },
  student: {
    headline: 'Your first win: make studying easier',
    tools: [byId('study'), byId('chat'), byId('image'), byId('prompt-factory')],
  },
  creator: {
    headline: 'Your first win: create something to publish',
    tools: [byId('image'), byId('prompt-factory'), byId('chat'), byId('webdesign')],
  },
  business: {
    headline: 'Your first win: automate a real task',
    tools: [byId('chat'), byId('contract'), byId('prompt-factory'), byId('webdesign')],
  },
};

const PERSONA_ICON: Record<PersonaType, typeof Wand2> = {
  general: Sparkles,
  security: Shield,
  student: GraduationCap,
  creator: Palette,
  business: Briefcase,
};

type OnboardingStep = 'welcome' | 'pick-persona' | 'pick-tool' | 'achievement';

export function GuidedOnboarding() {
  const navigate = useNavigate();
  const { onboarding, loading, completeOnboarding } = useOnboarding();
  const { trackEvent } = useAnalytics();
  const [currentStep, setCurrentStep] = useState<OnboardingStep>('welcome');
  const [selectedTool, setSelectedTool] = useState<ToolOption | null>(null);
  const { persona, setPersona } = usePersona();
  const [selectedPersona, setSelectedPersona] = useState<PersonaType>('general');
  const personaPlan = FIRST_WIN[selectedPersona];
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check if this is a first visit (no onboarding completed)
    if (!loading && !onboarding.completed) {
      const hasSeenOnboarding = localStorage.getItem('geniebot_guided_onboarding_seen');
      if (!hasSeenOnboarding) {
        setIsVisible(true);
        trackEvent('guided_onboarding_started');
      }
    }
  }, [loading, onboarding.completed]);

  const handleSelectTool = (tool: ToolOption) => {
    setSelectedTool(tool);
    trackEvent('onboarding_tool_selected', { tool: tool.id });
  };

  const handleTryTool = () => {
    if (!selectedTool) return;
    
    localStorage.setItem('geniebot_guided_onboarding_seen', 'true');
    localStorage.setItem('geniebot_first_tool', selectedTool.id);
    
    // Fire confetti
    
    // Fire confetti
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: ['#8B5CF6', '#06B6D4', '#10B981', '#F59E0B'],
    });
    
    setCurrentStep('achievement');
    trackEvent('onboarding_first_success', { tool: selectedTool.id });
  };

  const handleComplete = async () => {
    await completeOnboarding();
    setIsVisible(false);
    
    if (selectedTool) {
      navigate(selectedTool.route);
    }
    
    trackEvent('guided_onboarding_completed', { tool: selectedTool?.id });
  };

  const handleSkip = () => {
    localStorage.setItem('geniebot_guided_onboarding_seen', 'true');
    setIsVisible(false);
    trackEvent('guided_onboarding_skipped', { step: currentStep });
  };

  if (!isVisible) return null;

  const stepIndex =
    currentStep === 'welcome' ? 1 : currentStep === 'pick-persona' ? 2 : currentStep === 'pick-tool' ? 3 : 4;
  const progressValue = stepIndex * 25;

  const handleSelectPersona = async (next: PersonaType) => {
    setSelectedPersona(next);
    setSelectedTool(null);
    trackEvent('onboarding_persona_selected', { persona: next });
    await setPersona(next);
    setCurrentStep('pick-tool');
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: 'spring', duration: 0.5 }}
          className="w-full max-w-2xl"
        >
          <Card className="relative overflow-hidden">
            {/* Animated background gradient */}
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5" />
            
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-4 top-4 z-10"
              onClick={handleSkip}
            >
              <X className="h-4 w-4" />
            </Button>

            <CardHeader className="relative">
              <div className="flex items-center gap-2 mb-4">
                <Progress value={progressValue} className="h-2" />
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  Step {stepIndex}/4
                </span>
              </div>
            </CardHeader>

            <CardContent className="relative space-y-6">
              <AnimatePresence mode="wait">
                {/* Step 1: Welcome */}
                {currentStep === 'welcome' && (
                  <motion.div
                    key="welcome"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="text-center space-y-6"
                  >
                    <motion.div
                      animate={{ 
                        scale: [1, 1.1, 1],
                        rotate: [0, 5, -5, 0],
                      }}
                      transition={{ duration: 2, repeat: Infinity }}
                      className="inline-flex p-4 rounded-full bg-gradient-to-br from-primary/20 to-accent/20"
                    >
                      <Sparkles className="h-12 w-12 text-primary" />
                    </motion.div>
                    
                    <div>
                      <h2 className="text-2xl font-bold mb-2">Welcome to GenieBot! 🎉</h2>
                      <p className="text-muted-foreground">
                        Your AI-powered productivity companion. Let's get you started in just 3 quick steps.
                      </p>
                    </div>

                    <div className="flex gap-4 justify-center pt-4">
                      <Button variant="outline" onClick={handleSkip}>
                        Skip Tour
                      </Button>
                      <Button onClick={() => setCurrentStep('pick-persona')} className="gap-2">
                        Let's Go
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </motion.div>
                )}

                {/* Step 2: Who are you? */}
                {currentStep === 'pick-persona' && (
                  <motion.div
                    key="pick-persona"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="space-y-6"
                  >
                    <div className="text-center">
                      <h2 className="text-2xl font-bold mb-2">What brings you here?</h2>
                      <p className="text-muted-foreground">
                        We'll point you at one thing that pays off right away.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {(Object.keys(FIRST_WIN) as PersonaType[]).map((key) => {
                        const Icon = PERSONA_ICON[key];
                        const info = PERSONAS[key];
                        const isSelected = selectedPersona === key && persona === key;
                        return (
                          <motion.button
                            key={key}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => handleSelectPersona(key)}
                            className={cn(
                              'p-4 rounded-xl border-2 text-left transition-all focus-ring',
                              isSelected ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50',
                            )}
                          >
                            <div className="flex items-start gap-3">
                              <Icon className={cn('h-5 w-5 mt-0.5', info.color)} aria-hidden="true" />
                              <div className="min-w-0">
                                <h3 className="font-medium">{info.name}</h3>
                                <p className="text-xs text-muted-foreground mt-1">{info.description}</p>
                              </div>
                            </div>
                          </motion.button>
                        );
                      })}
                    </div>

                    <div className="flex gap-4 justify-center pt-2">
                      <Button variant="outline" onClick={() => setCurrentStep('welcome')}>
                        Back
                      </Button>
                      <Button variant="ghost" onClick={handleSkip}>
                        Skip
                      </Button>
                    </div>
                  </motion.div>
                )}

                {/* Step 3: Pick a Tool */}
                {currentStep === 'pick-tool' && (
                  <motion.div
                    key="pick-tool"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="space-y-6"
                  >
                    <div className="text-center">
                      <h2 className="text-2xl font-bold mb-2">Pick Your First Tool</h2>
                      <p className="text-muted-foreground">{personaPlan.headline}</p>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      {personaPlan.tools.map((tool) => {
                        const Icon = tool.icon;
                        const isSelected = selectedTool?.id === tool.id;
                        
                        return (
                          <motion.button
                            key={tool.id}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => handleSelectTool(tool)}
                            className={cn(
                              'p-4 rounded-xl border-2 text-left transition-all',
                              isSelected 
                                ? 'border-primary bg-primary/5 shadow-lg' 
                                : 'border-border hover:border-primary/50'
                            )}
                          >
                            <div className={cn(
                              'w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br mb-3',
                              tool.color
                            )}>
                              <Icon className="h-5 w-5 text-white" />
                            </div>
                            <h3 className="font-medium">{tool.name}</h3>
                            <p className="text-xs text-muted-foreground mt-1">
                              {tool.description}
                            </p>
                            {isSelected && (
                              <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                className="absolute top-2 right-2"
                              >
                                <Check className="h-5 w-5 text-primary" />
                              </motion.div>
                            )}
                          </motion.button>
                        );
                      })}
                    </div>

                    <div className="flex gap-4 justify-center pt-4">
                      <Button variant="outline" onClick={() => setCurrentStep('pick-persona')}>
                        Back
                      </Button>
                      <Button 
                        onClick={handleTryTool}
                        disabled={!selectedTool}
                        className="gap-2"
                      >
                        <Zap className="h-4 w-4" />
                        Try {selectedTool?.name || 'Selected Tool'}
                      </Button>
                    </div>
                  </motion.div>
                )}

                {/* Step 3: Achievement */}
                {currentStep === 'achievement' && (
                  <motion.div
                    key="achievement"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    className="text-center space-y-6"
                  >
                    <motion.div
                      initial={{ y: -20 }}
                      animate={{ y: 0 }}
                      className="relative inline-block"
                    >
                      <motion.div
                        animate={{ 
                          rotate: [0, 10, -10, 0],
                        }}
                        transition={{ duration: 0.5 }}
                        className="inline-flex p-6 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 shadow-xl"
                      >
                        <Trophy className="h-16 w-16 text-white" />
                      </motion.div>
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.3 }}
                        className="absolute -top-2 -right-2"
                      >
                        <PartyPopper className="h-8 w-8 text-primary" />
                      </motion.div>
                    </motion.div>

                    <div>
                      <h2 className="text-2xl font-bold mb-2">Achievement Unlocked! 🏆</h2>
                      <Badge className="mb-4 bg-gradient-to-r from-yellow-500 to-amber-500">
                        <Star className="h-3 w-3 mr-1" />
                        First Steps
                      </Badge>
                      <p className="text-muted-foreground">
                        You're ready to explore GenieBot! Here's a suggested next step:
                      </p>
                    </div>

                    <Card className="bg-muted/50">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                          <div className="p-3 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500">
                            <Sparkles className="h-6 w-6 text-white" />
                          </div>
                          <div className="text-left">
                            <p className="font-medium">Try GenieBot Avatar</p>
                            <p className="text-sm text-muted-foreground">
                              Interactive AI with face tracking
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <div className="flex gap-4 justify-center pt-4">
                      <Button variant="outline" onClick={handleComplete}>
                        Go to Dashboard
                      </Button>
                      <Button 
                        onClick={() => {
                          handleComplete();
                          navigate('/geniebot-particles');
                        }}
                        className="gap-2"
                      >
                        Try Avatar
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </CardContent>
          </Card>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
